import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Trash2, ExternalLink, Maximize2, X } from "lucide-react";

function getEmbedUrl(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com")) {
      const id = u.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (u.hostname.includes("youtu.be")) return `https://www.youtube.com/embed${u.pathname}`;
    if (u.hostname.includes("youtube.com") && u.pathname.includes("/shorts/")) {
      const id = u.pathname.split("/shorts/")[1].split("/")[0];
      return `https://www.youtube.com/embed/${id}`;
    }
    if (u.hostname.includes("instagram.com")) {
      return `https://www.instagram.com${u.pathname.replace(/\/$/, "")}/embed`;
    }
    if (u.hostname.includes("vimeo.com")) return `https://player.vimeo.com/video/${u.pathname.replace("/", "")}`;
    return null;
  } catch { return null; }
}

/* ─── Fullscreen Video Modal ─── */
function VideoModal({ proof, onClose }) {
  const embed = getEmbedUrl(proof.url);

  // Close on Escape key
  const handleKey = useCallback((e) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener("keydown", handleKey);
    // Prevent body scroll when modal is open
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [handleKey]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
      data-testid="video-modal-overlay"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-ink/90 backdrop-blur-sm" />

      {/* Modal Content */}
      <div
        className="relative z-10 w-[95vw] max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 flex items-center gap-2 text-cream font-mono text-xs uppercase tracking-widest hover:text-marker-yellow transition-colors"
          data-testid="video-modal-close"
        >
          Close <X size={18} />
        </button>

        {/* Video — constrained to viewport height */}
        {embed ? (
          <div
            className="border-4 border-cream shadow-brutal bg-ink"
            style={{ aspectRatio: "16/9", maxHeight: "85vh", width: "100%", margin: "0 auto" }}
          >
            <iframe
              src={`${embed}?autoplay=1`}
              title={proof.title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </div>
        ) : (
          <div className="brutal-card p-10 text-center" style={{ background: "#F4F1EA" }}>
            <p className="font-hand text-2xl mb-4">This proof links to an external page.</p>
            <a
              href={proof.url}
              target="_blank"
              rel="noopener noreferrer"
              className="brutal-btn brutal-btn-yellow inline-flex"
            >
              Open in new tab <ExternalLink size={14} />
            </a>
          </div>
        )}

        {/* Title + Note below the video */}
        <div className="mt-4 px-1">
          <div className="font-display font-extrabold text-xl uppercase tracking-tight text-cream">
            {proof.title}
          </div>
          {proof.note && (
            <div className="font-hand text-xl text-cream/70 mt-1">{proof.note}</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Main LiveProofs Component ─── */
export default function LiveProofs({ user, isOwner, onUpdate }) {
  const { user: me } = useAuth();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: "", url: "", note: "" });
  const [activeProof, setActiveProof] = useState(null);
  const proofs = user.live_proofs || [];

  const add = async (e) => {
    e.preventDefault();
    if (!form.title || !form.url) return;
    const { data, error } = await supabase.from("live_proofs")
      .insert({ user_id: me.id, title: form.title, url: form.url, note: form.note })
      .select().single();
    if (error) { console.error(error); return; }
    onUpdate({ live_proofs: [...proofs, data] });
    setForm({ title: "", url: "", note: "" });
    setShowAdd(false);
  };

  const remove = async (id) => {
    const { error } = await supabase.from("live_proofs").delete().eq("id", id);
    if (error) { console.error(error); return; }
    onUpdate({ live_proofs: proofs.filter((p) => p.id !== id) });
  };

  return (
    <section className="brutal-card p-5 sm:p-7" data-testid="live-proofs">
      <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-display font-extrabold text-2xl sm:text-3xl uppercase tracking-tight flex items-center gap-2">
          <span className="rec-dot" /> Live <span className="highlight-marker">Proof</span>
        </h2>
        <span className="font-hand text-2xl text-marker-red">— show, don't tell</span>
      </div>

      {proofs.length === 0 && <p className="font-hand text-2xl text-ink/60 mb-4">No proofs yet — link a video that proves something real.</p>}

      <div className="grid sm:grid-cols-2 gap-4">
        {proofs.map((p) => {
          const embed = getEmbedUrl(p.url);
          const isActiveInModal = activeProof?.id === p.id;
          return (
            <div key={p.id} className="brutal-card-sm p-3 group" data-testid={`live-proof-${p.id}`}>
              {embed ? (
                <div className="border-2 border-ink mb-2 relative" style={{ aspectRatio: "16/9" }}>
                  {/* When this proof is open in fullscreen, show a thumbnail instead of
                      a second iframe to prevent both videos from playing simultaneously. */}
                  {isActiveInModal ? (
                    <div className="w-full h-full bg-ink/90 flex items-center justify-center">
                      <span className="font-mono text-xs uppercase tracking-widest text-cream/60">Playing in fullscreen…</span>
                    </div>
                  ) : (
                    <iframe src={embed} title={p.title} className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  )}
                  {/* Fullscreen expand button */}
                  {!isActiveInModal && (
                    <button
                      onClick={() => setActiveProof(p)}
                      className="absolute top-2 right-2 bg-ink/80 text-cream p-2 border-2 border-cream hover:bg-marker-yellow hover:text-ink hover:border-ink transition-all opacity-0 group-hover:opacity-100"
                      title="Watch fullscreen"
                      data-testid={`expand-proof-${p.id}`}
                    >
                      <Maximize2 size={16} />
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex gap-2 mb-2">
                  <a href={p.url} target="_blank" rel="noopener noreferrer" className="brutal-btn flex-1 justify-between text-xs">
                    Open <ExternalLink size={14} />
                  </a>
                  <button
                    onClick={() => setActiveProof(p)}
                    className="brutal-btn text-xs"
                    title="View details"
                    data-testid={`expand-proof-${p.id}`}
                  >
                    <Maximize2 size={14} />
                  </button>
                </div>
              )}
              <div className="font-display font-extrabold uppercase tracking-tight">{p.title}</div>
              {p.note && <div className="font-hand text-xl">{p.note}</div>}
              {isOwner && (
                <button onClick={() => remove(p.id)} className="brutal-btn text-xs mt-2" data-testid={`delete-proof-${p.id}`}>
                  <Trash2 size={14} /> Remove
                </button>
              )}
            </div>
          );
        })}
      </div>

      {isOwner && !showAdd && (
        <button onClick={() => setShowAdd(true)} className="brutal-btn brutal-btn-blue mt-4" data-testid="add-proof-btn">
          <Plus size={14} /> Add live proof
        </button>
      )}
      {isOwner && showAdd && (
        <form onSubmit={add} className="brutal-card-sm p-4 mt-4 space-y-3" data-testid="add-proof-form">
          <input className="brutal-input" placeholder="Title (e.g. Hackathon win demo)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="proof-title-input" />
          <input className="brutal-input" placeholder="YouTube / Instagram / Vimeo URL" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="proof-url-input" />
          <input className="brutal-input" placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="proof-note-input" />
          <div className="flex gap-2">
            <button type="submit" className="brutal-btn brutal-btn-yellow" data-testid="save-proof-btn">Save proof</button>
            <button type="button" onClick={() => setShowAdd(false)} className="brutal-btn">Cancel</button>
          </div>
        </form>
      )}

      {/* Fullscreen Video Modal */}
      {activeProof && (
        <VideoModal proof={activeProof} onClose={() => setActiveProof(null)} />
      )}
    </section>
  );
}
